/**
 * OverviewPage - Listings Search & Browse
 *
 * Migrated to BasePage architecture.
 * Demonstrates: Complex layout with filters, search, list/map views, mobile fullscreen mode.
 * Note: Mobile map mode uses fixed fullscreen layout (special case, no BasePage).
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  List,
  Loader2,
  Map,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from "lucide-react";

// Hooks & Contexts
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";
import {
  useTranslation,
  useIsMobile,
  useCurrentLocale,
  useDeduplicatedEffect,
} from "@/shared/hooks";

// New Design System
import { BasePage } from "@/shared/ui/layout";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button, LanguageSwitcher } from "@/shared/ui/components";

// Components
import {
  FilterPanel,
  ListingCard,
  ListingsMap,
  SearchBar,
} from "@/features/listings/components";
import { Header } from "@/shared/ui/layout";

// Constants
import { AppRoutes, DEFAULT_FILTER_STATE } from "@/shared/constants";

// Types
import type { SearchCriteria } from "@/features/listings/components/SearchBar";
import type { FilterState } from "@/shared/constants";
import type { Listing, ListingsSearchParams } from "@/shared/types";

/**
 * Convert FilterState (UI) to ListingsSearchParams (API) - snake_case
 * Note: locale must be added by caller (from i18n context)
 */
function filterStateToSearchParams(
  filters: FilterState,
): Omit<ListingsSearchParams, "locale"> {
  const params: Partial<ListingsSearchParams> = {};

  // Price range (snake_case)
  if (filters.rent_min > 0) params.rent_min = filters.rent_min;
  if (filters.rent_max < 500000) params.rent_max = filters.rent_max;
  params.include_management_fee = filters.include_management_fee;

  // Size range (snake_case)
  if (filters.size_min > 0) params.size_min = filters.size_min;
  if (filters.size_max < 200) params.size_max = filters.size_max;

  // Room layouts (snake_case)
  if (filters.room_layout.length > 0) {
    params.room_layout = filters.room_layout.join(",");
  }

  // Property types (snake_case)
  if (filters.property_type.length > 0) {
    params.property_type = filters.property_type.join(",");
  }

  // Building/location filters (snake_case)
  if (filters.building_age !== "any")
    params.building_age =
      filters.building_age as ListingsSearchParams["building_age"];
  if (filters.floor !== "any")
    params.floor = filters.floor as ListingsSearchParams["floor"];
  if (filters.walk_to_station !== "any")
    params.walk_to_station =
      filters.walk_to_station as ListingsSearchParams["walk_to_station"];
  if (filters.key_money !== "any")
    params.key_money = filters.key_money as ListingsSearchParams["key_money"];
  if (filters.deposit !== "any")
    params.deposit = filters.deposit as ListingsSearchParams["deposit"];

  // Amenities (snake_case)
  if (filters.air_conditioning) params.air_conditioning = true;
  if (filters.washer_hookup) params.washer_hookup = true;
  if (filters.washer_dryer_in_unit) params.washer_dryer_in_unit = true;
  if (filters.bath_toilet_separate) params.bath_toilet_separate = true;
  if (filters.balcony) params.balcony = true;
  if (filters.flooring_wood) params.flooring_wood = true;
  if (filters.internet_included) params.internet_included = true;
  if (filters.gas_stove) params.gas_stove = true;
  if (filters.ih_cooktop) params.ih_cooktop = true;
  if (filters.elevator) params.elevator = true;
  if (filters.auto_lock) params.auto_lock = true;
  if (filters.delivery_box) params.delivery_box = true;
  if (filters.parking) params.parking = true;
  if (filters.bicycle_parking) params.bicycle_parking = true;
  if (filters.pets_allowed) params.pets_allowed = true;
  if (filters.instruments_ok) params.instruments_ok = true;
  if (filters.foreigner_friendly) params.foreigner_friendly = true;
  if (filters.guarantor_required) params.guarantor_required = true;
  if (filters.move_in_within_30_days) params.move_in_within_30_days = true;

  return params;
}

/**
 * Parse initial filters from URL search params
 */
function parseInitialFilters(urlParams: URLSearchParams): Partial<FilterState> {
  const partial: Partial<FilterState> = {};

  // Note: prefecture, city, ward are passed via URL but not part of FilterState
  // They are handled separately in effectiveSearchParams

  // Parse price range
  const rentMin = urlParams.get("rentMin");
  if (rentMin) partial.rent_min = parseInt(rentMin, 10);

  const rentMax = urlParams.get("rentMax");
  if (rentMax) partial.rent_max = parseInt(rentMax, 10);

  // Parse room layout
  const roomLayout = urlParams.get("roomLayout");
  if (roomLayout) {
    partial.room_layout = roomLayout.split(",").filter(Boolean);
  }

  return partial;
}

export const OverviewPage = observer(function OverviewPage() {
  const t = useTranslation();
  const { authStore } = useStore();
  const { user } = authStore;
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const locale = useCurrentLocale(); // Get current locale from i18n context

  // Parse initial search criteria from URL (only once on mount)
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(() => {
    const prefecture = urlSearchParams.get("prefecture") || "Tokyo";
    const city = urlSearchParams.get("city") || "Tokyo";
    const ward = urlSearchParams.get("ward") || "";
    const moveInDateStr = urlSearchParams.get("moveInDate");
    const occupantsStr = urlSearchParams.get("occupants");

    return {
      prefecture,
      city,
      ward,
      moveInDate: moveInDateStr ? new Date(moveInDateStr) : null,
      occupants: occupantsStr ? parseInt(occupantsStr, 10) : 1,
    };
  });

  // Parse initial filters from URL (only once on mount)
  const [filters, setFilters] = useState<FilterState>(() => {
    const parsed = parseInitialFilters(urlSearchParams);
    return { ...DEFAULT_FILTER_STATE, ...parsed };
  });
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);

  // Determine if we should use mobile map mode (fullscreen, no scroll)
  const isMobileMapMode = isMobile && viewMode === "map";

  // Prevent body scroll when in mobile map mode
  useEffect(() => {
    if (isMobileMapMode) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isMobileMapMode]);

  // Convert UI filters to API params (include locale from i18n context)
  const searchParams = useMemo(
    () => ({ locale, ...filterStateToSearchParams(filters) }),
    [filters, locale],
  );

  // Add location from URL to search params
  const effectiveSearchParams = useMemo(() => {
    const params: any = { ...searchParams };
    const prefecture = urlSearchParams.get("prefecture");
    const city = urlSearchParams.get("city");
    const ward = urlSearchParams.get("ward");
    if (prefecture) params.prefecture = prefecture;
    if (city) params.city = city;
    if (ward) params.ward = ward;
    return params;
  }, [searchParams, urlSearchParams]);

  // ListingsStore Integration
  const { listingsStore } = useStore();
  const {
    listings,
    loading: isLoading,
    error,
    totalCount,
    hasMore,
  } = listingsStore;

  // Sync filters to store on mount/change
  useDeduplicatedEffect(() => {
    // Only fetch if params have changed or initial load
    listingsStore.search(effectiveSearchParams);
  }, [effectiveSearchParams]);

  // Wrappers for store actions
  const loadMore = useCallback(() => {
    listingsStore.loadMore();
  }, []);

  const refresh = useCallback(() => {
    listingsStore.search(effectiveSearchParams);
  }, [effectiveSearchParams]);

  // We no longer need updateFilters/resetApiFilters as modifying searchParams triggers the effect above
  // isLoadingMore is roughly equivalent to isLoading && listings.length > 0 in this store's simple state
  const isLoadingMore = isLoading && listings.length > 0;

  // Handle filter changes - check if it's a reset (DEFAULT_FILTER_STATE)
  const handleFiltersChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);

      // Check if this is a reset to defaults
      const isReset =
        JSON.stringify(newFilters) === JSON.stringify(DEFAULT_FILTER_STATE);

      if (isReset) {
        // Reset local query params
        // Effect will handle refetch
      } else {
        // Effect will handle refetch
      }
    },
    [locale, urlSearchParams],
  );

  // Handle search criteria change from SearchBar
  const handleSearchCriteriaChange = useCallback(
    (newCriteria: SearchCriteria) => {
      setSearchCriteria(newCriteria);

      // Update URL and refetch with new location (include locale)
      const newParams: any = { locale, ...filterStateToSearchParams(filters) };
      newParams.prefecture = newCriteria.prefecture;
      newParams.city = newCriteria.city;
      if (newCriteria.ward) newParams.ward = newCriteria.ward;

      // Note: moveInDate and occupants are for UI context, not currently filtered on backend
      // Future: add availableFrom filter based on moveInDate

      // Effect will handle refetch when URL params change (which updates effectiveSearchParams)

      // Update URL to reflect new search criteria
      const urlParams = new URLSearchParams();
      urlParams.set("prefecture", newCriteria.prefecture);
      urlParams.set("city", newCriteria.city);
      if (newCriteria.ward) urlParams.set("ward", newCriteria.ward);
      if (newCriteria.moveInDate) {
        const dateStr = newCriteria.moveInDate.toISOString().split("T")[0];
        if (dateStr) urlParams.set("moveInDate", dateStr);
      }
      if (newCriteria.occupants > 1) {
        urlParams.set("occupants", String(newCriteria.occupants));
      }

      navigate(`/overview?${urlParams.toString()}`, { replace: true });
    },
    [locale, filters, navigate],
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handleListingClick = (listing: Listing) => {
    // Navigate to listing detail page
    navigate(`/listings/${listing.id}`);
  };

  const handleListingHover = (listing: Listing | null) => {
    setSelectedListing(listing);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const scrolledToBottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 200;

      if (scrolledToBottom && hasMore && !isLoadingMore) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore],
  );

  // Back button component for header left slot
  const backButton = (
    <button
      onClick={handleBack}
      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label={t("back")}
    >
      <ArrowLeft className="w-5 h-5 text-gray-600" />
    </button>
  );

  // Mobile map view - fullscreen layout (SPECIAL CASE - cannot use BasePage)
  if (isMobileMapMode) {
    return (
      <Box className="fixed inset-0 z-50 flex flex-col bg-gray-50">
        {/* Compact Header for mobile map */}
        <Header
          position="relative"
          variant="solid"
          showShadow
          showBorder
          leftContent={backButton}
          rightContent={
            <Box className="flex items-center gap-2">
              {/* Filter button */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowMobileFilters(true)}
                aria-label={t("filtersTitle")}
              >
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
              {/* Switch to list view */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="w-4 h-4 mr-1" />
                <Text as="span" variant="body" className="text-sm">
                  {t("overviewListView")}
                </Text>
              </Button>
            </Box>
          }
        />

        {/* Results count bar */}
        <Box className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
          <Text variant="body" className="text-sm text-gray-600">
            {isLoading
              ? t("overviewLoading")
              : `${totalCount} ${t("overviewResults")}`}
          </Text>
          <Button
            variant="ghost"
            size="icon"
            onClick={refresh}
            disabled={isLoading}
            aria-label="Refresh"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-600 ${isLoading ? "animate-spin" : ""}`}
            />
          </Button>
        </Box>

        {/* Mobile Filters Drawer - Also available in map mode */}
        {showMobileFilters && (
          <Box className="fixed inset-0 z-[60]">
            {/* Backdrop */}
            <Box
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowMobileFilters(false)}
            />
            {/* Drawer */}
            <Box className="absolute left-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClose={() => setShowMobileFilters(false)}
              />
            </Box>
          </Box>
        )}

        {/* Map fills remaining space */}
        <Box className="flex-1 min-h-0">
          <ListingsMap
            listings={listings}
            selectedListing={selectedListing}
            onListingSelect={handleListingClick}
            onListingHover={handleListingHover}
            onListingClick={handleListingClick}
            isMobile={true}
            className="h-full"
          />
        </Box>
      </Box>
    );
  }

  // Standard layout (list view or desktop map view) - uses BasePage
  return (
    <BasePage
      title={t("overviewResultsTitle")}
      description="Browse available apartments and properties"
      maxWidth="full"
      background="gray"
      padding="md"
      headerVariant="solid"
      headerProps={{
        leftContent: backButton,
        rightContent: (
          <Box className="flex items-center gap-4">
            <LanguageSwitcher className="text-gray-700" />
            {user ? (
              <Link to={AppRoutes.dashboard}>
                <Button variant="primary" size="sm">
                  {t("dashboard")}
                </Button>
              </Link>
            ) : (
              <Link to={AppRoutes.login}>
                <Button variant="primary" size="sm">
                  {t("signIn")}
                </Button>
              </Link>
            )}
          </Box>
        ),
      }}
    >
      <Box className="max-w-7xl mx-auto">
        {/* Search Bar - Above filters and results */}
        <Box className="mb-6">
          <SearchBar
            criteria={searchCriteria}
            onChange={handleSearchCriteriaChange}
          />
        </Box>

        <Box className="flex gap-6">
          {/* Left Sidebar - Filters (Desktop) */}
          <Box as="aside" className="hidden lg:block w-80 shrink-0">
            <Card
              variant="default"
              padding="none"
              className="shadow-lg overflow-hidden"
            >
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </Card>
          </Box>

          {/* Mobile Filters Drawer */}
          {showMobileFilters && (
            <Box className="fixed inset-0 z-50 lg:hidden">
              {/* Backdrop */}
              <Box
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowMobileFilters(false)}
              />
              {/* Drawer */}
              <Box className="absolute left-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
                <FilterPanel
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClose={() => setShowMobileFilters(false)}
                />
              </Box>
            </Box>
          )}

          {/* Main Content Area */}
          <Box as="main" className="flex-1 min-w-0">
            {/* Mobile Controls Bar - Only visible on mobile */}
            <Box className="lg:hidden flex items-center justify-between gap-2 mb-4">
              {/* Mobile filter toggle */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                aria-label={t("filtersTitle")}
              >
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                <Text as="span" variant="body" className="text-sm font-medium">
                  {t("filtersTitle")}
                </Text>
              </Button>

              {/* Mobile View Mode Toggle */}
              <Box className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={viewMode === "list" ? "bg-white shadow-sm" : ""}
                >
                  <List className="w-4 h-4 mr-1" />
                  <Text as="span" className="hidden sm:inline text-sm">
                    {t("overviewListView")}
                  </Text>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode("map")}
                  className={viewMode === "map" ? "bg-white shadow-sm" : ""}
                >
                  <Map className="w-4 h-4 mr-1" />
                  <Text as="span" className="hidden sm:inline text-sm">
                    {t("overviewMapView")}
                  </Text>
                </Button>
              </Box>
            </Box>

            {/* Results Header */}
            <Card variant="default" padding="md" className="shadow-sm mb-4">
              <Box className="flex items-center justify-between gap-4">
                {/* Left: Title + Count */}
                <Box className="min-w-0">
                  <Text variant="h4" className="text-gray-900">
                    {t("overviewResultsTitle")}
                  </Text>
                  <Text variant="body" className="text-sm text-gray-500 mt-1">
                    {isLoading ? (
                      t("overviewLoading")
                    ) : error ? (
                      <Text as="span" className="text-red-600">
                        {error}
                      </Text>
                    ) : (
                      `${totalCount} ${t("overviewResults")}`
                    )}
                  </Text>
                </Box>

                {/* Right: View toggle (desktop) + Refresh */}
                <Box className="flex items-center gap-3 shrink-0">
                  {/* Desktop View Mode Toggle */}
                  <Box className="hidden lg:flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewMode("list")}
                      className={
                        viewMode === "list" ? "bg-white shadow-sm" : ""
                      }
                    >
                      <List className="w-4 h-4 mr-1" />
                      <Text as="span" variant="body" className="text-sm">
                        {t("overviewListView")}
                      </Text>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewMode("map")}
                      className={viewMode === "map" ? "bg-white shadow-sm" : ""}
                    >
                      <Map className="w-4 h-4 mr-1" />
                      <Text as="span" variant="body" className="text-sm">
                        {t("overviewMapView")}
                      </Text>
                    </Button>
                  </Box>

                  {/* Refresh button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={refresh}
                    disabled={isLoading}
                    aria-label="Refresh"
                    title="Refresh"
                    className="border border-gray-200"
                  >
                    <RefreshCw
                      className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`}
                    />
                  </Button>
                </Box>
              </Box>
            </Card>

            {viewMode === "list" ? (
              /* List View */
              <Box className="space-y-4">
                {/* Loading State */}
                {isLoading && listings.length === 0 && (
                  <Card variant="default" padding="lg" className="shadow-sm">
                    <Box className="text-center">
                      <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
                      <Text variant="body" color="secondary">
                        {t("overviewLoading")}
                      </Text>
                    </Box>
                  </Card>
                )}

                {/* Error State */}
                {error && listings.length === 0 && (
                  <Card
                    variant="default"
                    padding="lg"
                    className="shadow-sm border-red-200"
                  >
                    <Box className="text-center">
                      <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <Text variant="h4" className="mb-2">
                        {t("overviewErrorTitle")}
                      </Text>
                      <Text variant="body" className="text-red-600 mb-4">
                        {error}
                      </Text>
                      <Button variant="primary" onClick={refresh}>
                        {t("overviewRetry")}
                      </Button>
                    </Box>
                  </Card>
                )}

                {/* Listings Grid */}
                {listings.length > 0 && (
                  <Box
                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
                    onScroll={handleScroll}
                  >
                    {listings.map((listing) => (
                      <ListingCard
                        key={listing.id}
                        listing={listing}
                        onClick={handleListingClick}
                        onHover={handleListingHover}
                        isSelected={selectedListing?.id === listing.id}
                      />
                    ))}
                  </Box>
                )}

                {/* Load More */}
                {hasMore && !isLoading && (
                  <Box className="text-center py-4">
                    <Button
                      variant="primary"
                      onClick={loadMore}
                      disabled={isLoadingMore}
                      loading={isLoadingMore}
                    >
                      {isLoadingMore
                        ? t("overviewLoadingMore")
                        : t("overviewLoadMore")}
                    </Button>
                  </Box>
                )}

                {/* Empty State */}
                {!isLoading && !error && listings.length === 0 && (
                  <Card variant="default" padding="lg" className="shadow-sm">
                    <Box className="text-center">
                      <Box className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-10 h-10 text-gray-400" />
                      </Box>
                      <Text variant="h4" className="mb-2">
                        {t("overviewEmptyTitle")}
                      </Text>
                      <Text
                        variant="body"
                        color="secondary"
                        className="max-w-md mx-auto"
                      >
                        {t("overviewEmptyDesc")}
                      </Text>
                    </Box>
                  </Card>
                )}
              </Box>
            ) : (
              /* Desktop Map View - Fixed height, page scrollable */
              <Card
                variant="default"
                padding="none"
                className="shadow-sm overflow-hidden"
              >
                <ListingsMap
                  listings={listings}
                  selectedListing={selectedListing}
                  onListingSelect={handleListingClick}
                  onListingHover={handleListingHover}
                  onListingClick={handleListingClick}
                  isMobile={false}
                  className="h-[calc(100vh-280px)] min-h-[500px]"
                />
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </BasePage>
  );
});
